import { useState } from "react";
import Sidebar from "../common/Sidebar";
import Header from "../common/Header";
import SmsCreateTemplateModal from "./SmsCreateTemplateModal";
import SmsMessageViewModal from "./SmsMessageViewModal";
import PageWrapper from "../common/PageWrapper";
import { deleteTemplate } from "../../../services/smsTemplateServices";
import toast from "react-hot-toast";
import { Edit, Trash2 } from "lucide-react";
import DeleteConfirmationModal from "../members/DeleteMemberModal";

const SmsTemplatesHandler = ({ template, onRefresh, refetch }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const handleEdit = (item) => {
    setSelected(item);
    setEditMode(true);
    setOpenModal(true);
  };

  const handleCreate = () => {
    setSelected(null);
    setEditMode(false);
    setOpenModal(true);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    toast
      .promise(deleteTemplate(deleteItem._id), {
        loading: "Deleting template...",
        success: "Template deleted successfully!",
        error: (err) => err.message || "Failed to delete template",
      })
      .then(() => {
        setOpenDelete(false);
        setDeleteItem(null);
        onRefresh?.();
      });
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setSelected(null);
    setEditMode(false);
    onRefresh?.();
  };

  return (
    <>
      <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
        <div className="h-full">
          <div className="sticky top-0 h-screen text-white">
            <Sidebar />
          </div>
        </div>

        <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
          <PageWrapper>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-4 overflow-y-auto">
                <Header
                  title="Templates"
                  desc="Here we can create and manage templates"
                />

                <section className="flex justify-end w-full mb-6 mt-4">
                  <div
                    onClick={handleCreate}
                    className="bg-[#4F22D8] cursor-pointer border border-[#0000003D] text-white px-4 py-2 text-sm font-semibold rounded-[10px] hover:opacity-90 transition"
                  >
                    + Create Template
                  </div>
                </section>

                <section className="bg-white rounded-xl overflow-hidden border border-[#D5D5D5] mt-10">
                  <div className="overflow-auto">
                    <table className="min-w-full text-sm font-medium text-black table-fixed border-l border-[#D5D5D5]">
                      <thead className="bg-[#4F22D8] text-white font-medium text-sm">
                        <tr>
                          <th className="px-4 py-3 text-left w-[80px] border-r border-[#D5D5D5]">
                            No
                          </th>
                          <th className="px-4 py-3 text-left w-[160px] border-r border-[#D5D5D5]">
                            Template Name
                          </th>
                          <th className="px-4 py-3 text-center w-[500px] border-r border-[#D5D5D5]">
                            Template Id
                          </th>
                          <th className="px-4 py-3 text-center w-[500px] border-r border-[#D5D5D5]">
                            Sample Message
                          </th>
                          <th className="px-4 py-3 text-center w-[500px] border-r border-[#D5D5D5]">
                            Placeholders
                          </th>
                          <th className="px-4 py-3 text-center w-[150px] border-r border-[#D5D5D5]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {template.map((item, idx) => (
                          <tr
                            key={item._id}
                            className="text-sm text-black font-normal border-b border-[#B0B0B075] last:border-0"
                          >
                            <td className="px-4 py-3 border-l border-[#D5D5D5]">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3 text-center border-l border-[#D5D5D5]">
                              {item.templateName}
                            </td>
                            <td className="px-4 py-3 border-l text-center border-[#D5D5D5]">
                              {item.templateId}
                            </td>
                            <td className="px-4 py-3 border-l text-left border-[#D5D5D5]">
                              {item.sampleMessage}
                            </td>
                            <td className="px-4 py-3 border-l border-[#E0E0E0]">
                              {item.placeholders &&
                              item.placeholders.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {item.placeholders.map(
                                    (placeholder, pidx) => (
                                      <span
                                        key={pidx}
                                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                                      >
                                        <span className="font-mono">
                                          {`{{${placeholder.position}}}`}
                                        </span>
                                        <span className="text-xs">
                                          {placeholder.label}
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs italic">
                                  No placeholders
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 border-l border-[#D5D5D5] text-center">
                              <div className="flex gap-2 justify-center">
                                <span
                                  onClick={() => {
                                    setSelected(item);
                                    setOpenView(true);
                                  }}
                                  className="bg-[#E0E3FF] cursor-pointer text-[#1E30D4] px-3 py-1 rounded-[10px] font-semibold text-xs shadow-sm hover:bg-[#D0D3EF] transition"
                                >
                                  View
                                </span>
                                <span
                                  onClick={() => handleEdit(item)}
                                  className="bg-[#FFF4E0] cursor-pointer text-[#D49B1E] px-3 py-1 rounded-[10px] font-semibold text-xs shadow-sm hover:bg-[#FFF0D0] transition flex items-center gap-1"
                                >
                                  <Edit size={12} />
                                  Edit
                                </span>
                                <span
                                  onClick={() => handleDelete(item)}
                                  className="bg-[#FFE0E0] cursor-pointer text-[#D41E1E] px-3 py-1 rounded-[10px] font-semibold text-xs shadow-sm hover:bg-[#FFD0D0] transition flex items-center gap-1"
                                >
                                  <Trash2 size={12} />
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            </div>
          </PageWrapper>
        </div>
      </div>

      {openModal && (
        <SmsCreateTemplateModal
          onClose={handleModalClose}
          editMode={editMode}
          templateData={selected}
        />
      )}

      <SmsMessageViewModal
        isOpen={openView}
        onClose={() => setOpenView(false)}
        data={selected}
      />

      <DeleteConfirmationModal
        isOpen={openDelete}
        onRequestClose={() => {
          setOpenDelete(false);
          setDeleteItem(null);
        }}
        onDelete={confirmDelete}
        itemName={deleteItem?.templateName}
        itemType="template"
      />
    </>
  );
};

export default SmsTemplatesHandler;
